import React, { useEffect, useState, useCallback, useRef } from "react";
import moment from "moment";
import { Typography } from "@mui/material";

const MessageTimer = ({ createdAt, onReachZero, onAlreadyReachZero }) => {
  const [remaining, setRemaining] = useState(0);
  const intervalRef = useRef(null);
  const onReachZeroRef = useRef(onReachZero);

  // Update ref when callback changes to avoid stale closures
  useEffect(() => {
    onReachZeroRef.current = onReachZero;
  }, [onReachZero]);

  // Method 1: Check if already reached zero
  const alreadyReached = useCallback(() => {
    const diff = moment().diff(moment(createdAt), "seconds");
    return diff >= 30;
  }, [createdAt]);

  // Memoized calculation function
  const calculateRemaining = useCallback(() => {
    const diff = moment().diff(moment(createdAt), "seconds");
    return Math.max(30 - diff, 0);
  }, [createdAt]);

  useEffect(() => {
    // Calculate initial remaining time
    const initialRemaining = calculateRemaining();
    setRemaining(initialRemaining);

    // If already expired, call onAlreadyReachZero callback and exit
    if (alreadyReached()) {
      onAlreadyReachZero?.();
      return;
    }

    // Set up interval only if not expired
    intervalRef.current = setInterval(() => {
      const left = calculateRemaining();
      setRemaining(left);
      
      // Method 2: Call onReachZero only when timer naturally reaches zero
      if (left <= 0) {
        clearInterval(intervalRef.current);
        onReachZeroRef.current?.(); // Only call when countdown reaches zero naturally
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [createdAt, calculateRemaining]);

  return (
    <Typography
      variant="caption"
      sx={{
        color: remaining <= 5 ? "#EF4444" : "#6B7280", // Red when <= 5 seconds
        fontSize: "12px",
        fontWeight: 500,
        transition: "color 0.3s ease", // Smooth color transition
      }}
    >
      {remaining}s remaining
    </Typography>
  );
};

export default MessageTimer;